<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission)

const loading    = ref(false)
const saving     = ref(false)
const kpis       = ref([])
const categories = ref([])
const showModal  = ref(false)
const editId     = ref(null)
const formError  = ref('')

const blankForm = () => ({
  title: '', description: '', unit: '', category: '',
  measurement_type: 'number', weight: '', target_value: '',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get('/hr/pms/kpis')
    kpis.value = res.data.data?.data ?? res.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadCategories = async () => {
  try {
    const res = await axios.get('/hr/pms/kpis/categories')
    categories.value = res.data.data ?? []
  } catch (e) { console.error(e) }
}

onMounted(() => { load(); loadCategories() })

const openCreate = () => {
  editId.value = null; form.value = blankForm(); formError.value = ''; showModal.value = true
}

const openEdit = (k) => {
  editId.value = k.id
  form.value = {
    title: k.title, description: k.description ?? '',
    unit: k.unit ?? '', category: k.category ?? '',
    measurement_type: k.measurement_type ?? 'number',
    weight: k.weight ?? '', target_value: k.target_value ?? '',
  }
  formError.value = ''; showModal.value = true
}

const save = async () => {
  if (!form.value.title) { formError.value = 'KPI title is required.'; return }
  try {
    saving.value = true; formError.value = ''
    if (editId.value) {
      await axios.put(`/hr/pms/kpis/${editId.value}`, form.value)
    } else {
      await axios.post('/hr/pms/kpis', form.value)
    }
    showModal.value = false
    load()
    loadCategories()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save'
  } finally { saving.value = false }
}

const deleteKpi = async (id) => {
  if (!confirm('Delete this KPI?')) return
  await axios.delete(`/hr/pms/kpis/${id}`)
  load()
}
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div class="toolbar">
        <h2 class="page-title">KPI Library</h2>
        <button v-if="permission?.manage_pms_kpis" class="btn-primary" @click="openCreate">+ New KPI</button>
      </div>

      <div v-if="loading" class="empty">Loading…</div>
      <div v-else-if="!kpis.length" class="empty">No KPIs defined yet. Build your KPI library to use in reviews.</div>
      <div v-else class="kpi-grid">
        <div v-for="k in kpis" :key="k.id" class="kpi-card">
          <div class="kpi-name">{{ k.title }}</div>
          <div v-if="k.category" class="kpi-cat">{{ k.category }}</div>
          <div class="kpi-meta">
            <span class="kpi-chip">{{ k.measurement_type }}</span>
            <span v-if="k.unit" class="kpi-chip">{{ k.unit }}</span>
            <span v-if="k.weight" class="kpi-chip">Weight: {{ k.weight }}%</span>
          </div>
          <div v-if="k.description" class="kpi-desc">{{ k.description }}</div>
          <div class="card-actions">
            <button v-if="permission?.manage_pms_kpis" class="btn-link" @click="openEdit(k)">Edit</button>
            <button v-if="permission?.manage_pms_kpis" class="btn-link btn-link--red" @click="deleteKpi(k.id)">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editId ? 'Edit' : 'New' }} KPI</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group form-group--full">
              <label>KPI Title *</label>
              <input v-model="form.title" placeholder="e.g. Sales Revenue Achievement" />
            </div>
            <div class="form-group">
              <label>Category</label>
              <select v-model="form.category">
                <option value="">— Select category —</option>
                <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                <option value="__custom__">+ Add new category…</option>
              </select>
            </div>
            <div v-if="form.category === '__custom__'" class="form-group">
              <label>New Category Name</label>
              <input
                placeholder="Type a new category name"
                @input="e => form.category = e.target.value || '__custom__'"
                :value="form.category === '__custom__' ? '' : form.category"
              />
            </div>
            <div class="form-group">
              <label>Measurement Type</label>
              <select v-model="form.measurement_type">
                <option value="number">Number</option>
                <option value="percentage">Percentage</option>
                <option value="rating">Rating</option>
                <option value="boolean">Yes / No</option>
              </select>
            </div>
            <div class="form-group">
              <label>Unit</label>
              <input v-model="form.unit" placeholder="e.g. %, NGN, units" />
            </div>
            <div class="form-group">
              <label>Weight (%)</label>
              <input type="number" v-model.number="form.weight" min="0" max="100" step="0.5" />
            </div>
            <div class="form-group">
              <label>Target Value</label>
              <input type="number" v-model="form.target_value" step="0.01" placeholder="Optional" />
            </div>
            <div class="form-group form-group--full">
              <label>Description</label>
              <textarea v-model="form.description" rows="2" placeholder="What does this KPI measure?" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.empty { text-align: center; color: #888; padding: 60px; font-size: 14px; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.kpi-card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 18px 20px; transition: box-shadow 0.15s; }
.kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
.kpi-name { font-size: 15px; font-weight: 700; color: #1a2a45; margin-bottom: 4px; }
.kpi-cat  { font-size: 12px; color: #888; margin-bottom: 8px; }
.kpi-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.kpi-chip { background: #eef4ff; color: #004AAD; font-size: 11.5px; font-weight: 600; padding: 2px 8px; border-radius: 999px; text-transform: capitalize; }
.kpi-desc { font-size: 12.5px; color: #888; margin-bottom: 8px; }
.card-actions { display: flex; gap: 8px; border-top: 1px solid #f0f3fa; padding-top: 10px; margin-top: 8px; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 580px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select, .form-group textarea { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; resize: vertical; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
